import { PlusIcon } from '@phosphor-icons/react'
import { useEffect, useRef, useState } from 'react'

import { useGetClientProfile } from '@/api/clients/get-client-profile'
import { useCreateIglooUser } from '@/api/keyless/igloo/create-igloo-user'
import { useGetIglooBuildingDevices } from '@/api/keyless/igloo/get-igloo-building-devices'
import { useGetIglooCustomPin } from '@/api/keyless/igloo/get-igloo-custom-pin'
import { useGetIglooDevices } from '@/api/keyless/igloo/get-igloo-devices'
import { useGetIglooPin } from '@/api/keyless/igloo/get-igloo-pin'
import { useIssueIglooPin } from '@/api/keyless/igloo/issue-igloo-pin'
import { EMPTY_IGLOO_CUSTOM_PIN } from '@/api/keyless/igloo/normalize-igloo-custom-pin.utils'
import { getEarliestKeyExpiry } from '@/api/keyless/igloo/normalize-igloo-devices'
import { EMPTY_IGLOO_PIN } from '@/api/keyless/igloo/normalize-igloo-pin'
import { useResendIglooCustomPinEmail } from '@/api/keyless/igloo/resend-igloo-custom-pin-email'
import { useResendIglooPinEmail } from '@/api/keyless/igloo/resend-igloo-pin-email'
import { useRevokeIglooCustomPin } from '@/api/keyless/igloo/revoke-igloo-custom-pin'
import { useRevokeIglooPin } from '@/api/keyless/igloo/revoke-igloo-pin'
import { useUpdateIglooDevices } from '@/api/keyless/igloo/update-igloo-devices'
import { useIsIglooWorksBuildingConnected } from '@/api/property-keyless/igloo-works/use-is-igloo-works-building-connected'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ChipSelect } from '@/components/ui/chip-select'
import { ConfirmationPopup } from '@/components/ui/confirmation-popup'
import { DatePickerInput } from '@/components/ui/date-picker-input'
import { Flex } from '@/components/ui/flex'
import { Icon } from '@/components/ui/icon'
import { Typography } from '@/components/ui/typography'
import { EMPTY_VALUE } from '@/constants/ui'
import { popupFactory, toastFactory } from '@/factories'
import { DirtyFieldChange } from '@/hooks'
import { formatDateOnly } from '@/utils/date'
import { getApiErrorMessage } from '@/utils/error'

import * as S from '../keyless-access.styled'
import { computeKeylessArrayDiff, computeKeylessDateDiff } from '../keyless-access.utils'
import { KeylessBuildingNotConnected } from '../keyless-access-building-not-connected'
import { KeylessTabSkeleton } from '../keyless-access-tab-skeleton'
import { IglooCustomPinBlock } from './igloo-custom-pin-block'
import { IglooPinBlock } from './igloo-pin-block'
import { IglooTabProps } from './igloo-tab.types'

const DAY_MS = 24 * 60 * 60 * 1000
const MAX_EXPIRATION_DAYS = 5 * 365

export const IglooTab = ({
  buildingId,
  clientId,
  clientName,
  canEdit,
  isEditing,
  onEditingChange,
  onRegisterSave,
  onRegisterGetChanges,
  onLoadingChange,
  onConnectionChange,
}: IglooTabProps) => {
  const { data: clientData } = useGetClientProfile({ clientId })
  const { isBuildingConnected, isLoading: isLoadingBuildingConnection } =
    useIsIglooWorksBuildingConnected(buildingId)
  const {
    data: devices,
    isLoading: devicesLoading,
    refetch: refetchDevices,
  } = useGetIglooDevices({
    clientId,
  })
  const algoPinAvailable = Boolean(clientData?.buildingId?.iglooWorkAlgoPinAvailable)
  const customPinAvailable = Boolean(clientData?.buildingId?.iglooWorkCustomPinAvailable)
  const { data: pin, isLoading: pinLoading } = useGetIglooPin({ clientId }, algoPinAvailable)
  const { data: customPin, isLoading: customPinLoading } = useGetIglooCustomPin(
    { clientId },
    customPinAvailable,
  )
  const { data: buildingDevices, isLoading: buildingDevicesLoading } = useGetIglooBuildingDevices(
    { buildingId: String(buildingId) },
    isEditing,
  )

  const { mutateAsync: createUser, isPending: isCreating } = useCreateIglooUser(clientId)
  const { mutateAsync: issuePin, isPending: isIssuing } = useIssueIglooPin(clientId)
  const { mutateAsync: revokePin, isPending: isRevoking } = useRevokeIglooPin(clientId)
  const { mutateAsync: resendEmail, isPending: isResending } = useResendIglooPinEmail(clientId)
  const { mutateAsync: revokeCustomPin, isPending: isRevokingCustomPin } =
    useRevokeIglooCustomPin(clientId)
  const { mutate: resendCustomPinEmail, isPending: isResendingCustomPin } =
    useResendIglooCustomPinEmail(clientId)
  const { mutateAsync: updateDevices } = useUpdateIglooDevices({ clientId })

  const [showConfirmCreate, setShowConfirmCreate] = useState(false)
  // null = "untouched"; the committed value is used until the user edits the field.
  const [selectedDoorIds, setSelectedDoorIds] = useState<string[] | null>(null)
  const [editedExpirationMs, setEditedExpirationMs] = useState<number | null | undefined>(undefined)
  const [minExpiration] = useState(() => new Date())
  const [maxExpiration] = useState(() => new Date(Date.now() + MAX_EXPIRATION_DAYS * DAY_MS))

  const keys = devices?.keys ?? []
  const pinData = pin ?? EMPTY_IGLOO_PIN
  const customPinData = customPin ?? EMPTY_IGLOO_CUSTOM_PIN
  const isTabLoading =
    devicesLoading || pinLoading || customPinLoading || isLoadingBuildingConnection
  const hasIglooData =
    keys.length > 0 || pinData.status !== 'none' || customPinData.status !== 'none'
  // Note: "connected" latches. Doors are editable now, so a resident can legitimately end up with
  // zero keys and no PIN — and a freshly created Igloo user starts that way. Deriving connection
  // from the key count alone would then collapse the tab back to "Create Igloo user" and hide the
  // Edit button, stranding exactly the case this tab exists for.
  const [wasConnected, setWasConnected] = useState(false)
  const connected = !isTabLoading && (hasIglooData || wasConnected)

  useEffect(() => {
    if (!isTabLoading && hasIglooData) setWasConnected(true)
  }, [hasIglooData, isTabLoading])

  useEffect(() => {
    onLoadingChange?.(isTabLoading)
    return () => onLoadingChange?.(false)
  }, [isTabLoading, onLoadingChange])

  // Note: the Edit affordance is offered to any role that may edit, not only when the resident
  // already holds a key. There is no endpoint that answers "does this client have an Igloo user"
  // (/igloowork/users is POST-only), so zero doors + no PIN is indistinguishable from having no
  // user at all — but PUT /igloowork/:clientId/devices works regardless, verified against staging
  // by assigning a door to a resident in exactly that state. Gating Edit on the key count is what
  // stranded a resident whose last door was removed: the write was always available, the UI just
  // hid it. If the client genuinely has no Igloo user the PUT surfaces the backend's error, and
  // the Create Igloo user action is still on screen next to it.
  useEffect(() => {
    if (isTabLoading) return
    onConnectionChange?.(isBuildingConnected && (connected || canEdit))
  }, [canEdit, connected, isBuildingConnected, isTabLoading, onConnectionChange])

  // The resident's real key expiry when the backend sends one, otherwise the lease dates the
  // legacy Igloo section used to seed this field.
  const earliestKeyExpiryMs = getEarliestKeyExpiry(keys)
  const leaseMs = [clientData?.leaseEnd, clientData?.moveOutDate].find(
    (ms): ms is number => typeof ms === 'number',
  )
  const committedExpirationMs = earliestKeyExpiryMs ?? leaseMs ?? null
  const committedExpiration = committedExpirationMs ? new Date(committedExpirationMs) : null
  const committedDoorIds = keys.map((k) => k.id)

  const activeDoorIds = selectedDoorIds ?? committedDoorIds
  const activeExpirationMs =
    editedExpirationMs === undefined ? committedExpirationMs : editedExpirationMs
  const activeExpiration = activeExpirationMs ? new Date(activeExpirationMs) : null
  const isExpirationDirty =
    editedExpirationMs !== undefined && editedExpirationMs !== committedExpirationMs

  const handleSaveDevices = async () => {
    try {
      const assigned = await updateDevices({
        devices: activeDoorIds,
        ...(isExpirationDirty && activeExpirationMs ? { expirationDate: activeExpirationMs } : {}),
      })
      // Note: a 200 does not mean the doors were assigned. A client with no Igloo user answers 200
      // with an empty array and changes nothing, which would otherwise show a success toast for a
      // request that did nothing. The response is the persisted set, so trust it over the status.
      const assignedIds = new Set(assigned.map((k) => k.id))
      const missing = activeDoorIds.filter((id) => !assignedIds.has(id))
      if (missing.length) {
        toastFactory.error(
          'Igloo did not accept the door assignment. The resident may not have an Igloo user yet — create one and try again.',
        )
        void refetchDevices()
        return
      }
      toastFactory.success('Igloo device access has been updated')
      void refetchDevices()
      onEditingChange(false)
    } catch (error) {
      toastFactory.error(getApiErrorMessage(error))
    }
  }

  // Note: the save handler is reached through a ref so the parent always invokes the latest one.
  // Registering the closure itself would freeze the untouched-field fallbacks (the committed doors
  // and expiry) at registration time, and a refetch mid-edit — staleTime is 0 and React Query
  // refetches on window focus — would then submit a stale door set the form is no longer showing.
  const saveRef = useRef(handleSaveDevices)
  saveRef.current = handleSaveDevices

  const confirmationRef = useRef('')
  confirmationRef.current = [
    'Are you sure you want to update the assigned doors for this resident?',
    isExpirationDirty && activeExpiration
      ? `All ${keys.length} existing door key${keys.length === 1 ? '' : 's'} will be re-issued with the new expiration date, and issued Igloo keys cannot be recalled.`
      : '',
  ]
    .filter(Boolean)
    .join(' ')

  useEffect(() => {
    if (!isEditing) {
      setSelectedDoorIds(null)
      setEditedExpirationMs(undefined)
      return
    }
    onRegisterSave?.(() =>
      popupFactory.open(ConfirmationPopup, {
        children: confirmationRef.current,
        confirmText: 'Yes, update',
        onConfirm: () => saveRef.current(),
        title: 'Confirm door assignment',
        variant: 'warning',
      }),
    )
    return () => onRegisterSave?.(null)
  }, [isEditing, onRegisterSave])

  const getChangesRef = useRef<() => DirtyFieldChange[]>(() => [])
  getChangesRef.current = () => {
    const doorNames = new Map(keys.map((k) => [k.id, k.name]))
    for (const device of buildingDevices?.devices ?? []) doorNames.set(device.id, device.name)
    const changes: DirtyFieldChange[] = []
    const doorDiff = computeKeylessArrayDiff(
      committedDoorIds,
      activeDoorIds,
      'Door',
      (id) => doorNames.get(id) ?? id,
    )
    if (doorDiff) changes.push(doorDiff)
    const dateDiff = isExpirationDirty
      ? computeKeylessDateDiff(committedExpiration, activeExpiration, 'Expiration Date')
      : null
    if (dateDiff) changes.push(dateDiff)
    return changes
  }

  useEffect(() => {
    if (!isEditing) {
      onRegisterGetChanges?.(null)
      return
    }
    onRegisterGetChanges?.(() => getChangesRef.current())
    return () => onRegisterGetChanges?.(null)
  }, [isEditing, onRegisterGetChanges])

  if (isTabLoading) return <KeylessTabSkeleton rowCount={3} />

  if (!isBuildingConnected) {
    return (
      <KeylessBuildingNotConnected
        title="Igloo Works isn't connected"
        message="This building's Igloo Works integration isn't connected right now. Contact your admin to reconnect it before managing this resident's access."
      />
    )
  }

  const handleCreateUser = async () => {
    try {
      await createUser()
      toastFactory.success('Igloo user has been successfully created')
      setShowConfirmCreate(false)
      // A new Igloo user holds no doors and no PIN yet, so nothing in the fetched data would mark
      // the tab as connected — latch it here or the user has no way to be assigned a first door.
      setWasConnected(true)
      void refetchDevices()
    } catch (error) {
      toastFactory.error(getApiErrorMessage(error))
    }
  }

  const handleIssue = async () => {
    try {
      await issuePin()
      toastFactory.success(
        'PIN code access issued successfully. The PIN was sent to the resident by email.',
      )
    } catch (error) {
      toastFactory.error(getApiErrorMessage(error))
    }
  }

  const handleRevoke = async () => {
    try {
      await revokePin()
      toastFactory.success('Unit PIN code access has been revoked')
    } catch (error) {
      toastFactory.error(getApiErrorMessage(error))
    }
  }

  const handleResend = async () => {
    try {
      await resendEmail()
      toastFactory.success('PIN email has been resent')
    } catch (error) {
      toastFactory.error(getApiErrorMessage(error))
    }
  }

  const handleRevokeCustomPin = async () => {
    await revokeCustomPin()
  }

  const expirationLabel = committedExpiration ? formatDateOnly(committedExpiration) : EMPTY_VALUE

  const pinBlocks = (
    <>
      {algoPinAvailable && (
        <IglooPinBlock
          pin={pinData}
          canEdit={canEdit}
          residentName={clientName}
          isIssuing={isIssuing}
          isRevoking={isRevoking}
          isResending={isResending}
          onIssue={handleIssue}
          onRevoke={handleRevoke}
          onResend={handleResend}
        />
      )}
      {customPinAvailable && (
        <IglooCustomPinBlock
          pin={customPinData}
          canEdit={canEdit}
          residentName={clientName}
          isRevoking={isRevokingCustomPin}
          isResending={isResendingCustomPin}
          onRevoke={handleRevokeCustomPin}
          onResend={() => resendCustomPinEmail()}
        />
      )}
    </>
  )

  // Note: checked before the not-connected branch so the door editor stays reachable for a
  // resident holding no keys — otherwise clicking Edit in that state would fall through to the
  // "Create Igloo user" row and there would be no way back to assigning a door.
  if (isEditing) {
    // Note: seeded with the resident's own keys so their chips carry door names from the first
    // frame — the building list arrives on its own schedule, and ChipSelect falls back to showing
    // the raw Igloo device id for any value it has no option for.
    const doorNamesById = new Map(keys.map((k) => [k.id, k.name]))
    for (const device of buildingDevices?.devices ?? []) doorNamesById.set(device.id, device.name)
    const doorOptions = [...doorNamesById].map(([value, label]) => ({ label, value }))

    return (
      <S.TabContent>
        <S.FieldRow>
          <Flex align="center" gap={6}>
            <Typography size="text-medium" color="primary500" as="span" whiteSpace="nowrap">
              Door
            </Typography>
          </Flex>
          <ChipSelect
            size="small"
            options={doorOptions}
            value={activeDoorIds}
            placeholder="Select Door..."
            loading={buildingDevicesLoading}
            onChange={setSelectedDoorIds}
          />
        </S.FieldRow>
        <S.FieldRow>
          <Flex align="center" gap={6}>
            <Typography size="text-medium" color="primary500" as="span" whiteSpace="nowrap">
              Expiration Date
            </Typography>
          </Flex>
          <DatePickerInput
            size="small"
            placeholder="MM/DD/YYYY"
            value={activeExpiration}
            minDate={minExpiration}
            maxDate={maxExpiration}
            onChange={(date) => setEditedExpirationMs(date ? date.getTime() : null)}
          />
        </S.FieldRow>

        {pinBlocks}
      </S.TabContent>
    )
  }

  if (!connected) {
    return (
      <S.TabContent>
        <S.FieldRow>
          <Flex align="center" gap={6}>
            <Typography size="text-medium" color="primary500" as="span" whiteSpace="nowrap">
              Iglooworks
            </Typography>
          </Flex>
          <Flex align="center" gap={32} justify="flex-end">
            {canEdit ? (
              <Button size="small" loading={isCreating} onClick={() => setShowConfirmCreate(true)}>
                Create Igloo user
                <Icon icon={PlusIcon} size="1.6rem" />
              </Button>
            ) : (
              <S.FieldValue>{EMPTY_VALUE}</S.FieldValue>
            )}
          </Flex>
        </S.FieldRow>
        {pinBlocks}
        {showConfirmCreate && (
          <ConfirmationPopup
            title="Confirm new user creation"
            variant="warning"
            confirmText="Yes, create"
            onClose={() => setShowConfirmCreate(false)}
            onConfirm={handleCreateUser}
          >
            Are you sure you want to create a new Iglooworks user?
          </ConfirmationPopup>
        )}
      </S.TabContent>
    )
  }

  return (
    <S.TabContent>
      <S.FieldRow>
        <Flex align="center" gap={6}>
          <Typography size="text-medium" color="primary500" as="span" whiteSpace="nowrap">
            Door
          </Typography>
        </Flex>
        <S.FieldValue>
          {keys.length > 0 ? (
            <Flex wrap="wrap" gap={6}>
              {keys.map((k) => (
                <Badge key={k.id} color="blue" size="small" variant="light">
                  {k.name}
                </Badge>
              ))}
            </Flex>
          ) : (
            EMPTY_VALUE
          )}
        </S.FieldValue>
      </S.FieldRow>
      <S.FieldRow>
        <Flex align="center" gap={6}>
          <Typography size="text-medium" color="primary500" as="span" whiteSpace="nowrap">
            Expiration Date
          </Typography>
        </Flex>
        <S.FieldValue>{expirationLabel}</S.FieldValue>
      </S.FieldRow>

      {pinBlocks}
    </S.TabContent>
  )
}
